let counter = 0;

/**
 * Generate an identifier suitable for demo data (messages, conversations, etc.).
 *
 * IMPORTANT: this generator is intentionally **deterministic** so identifiers
 * produced at module load time match between the SSR pass and the client
 * hydration pass — `crypto.randomUUID()` and `Date.now()` would both produce
 * different values on server and client, breaking React hydration whenever a
 * demo seeds its store at the module top level (Playwright Bug A).
 *
 * Demos are not real apps; the absence of true randomness has no security
 * implications and makes the docs preview reproducible.
 */
export function randomId() {
  counter += 1;
  return `demo-id-${counter.toString(36)}`;
}

/**
 * Explicit alias for callers that want to make the deterministic intent
 * obvious at the call site (e.g. when generating seed IDs at module load).
 */
export const deterministicId = randomId;

export function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function splitText(text, size = 18) {
  const chunks = [];

  for (let index = 0; index < text.length; index += size) {
    chunks.push(text.slice(index, index + size));
  }

  return chunks.length === 0 ? [''] : chunks;
}

export function createTextResponseChunks(messageId, text, options) {
  const finishReason = options?.finishReason ?? 'stop';
  const partId = `${messageId}-text`;

  return [
    {
      type: 'start',
      messageId,
      ...(options?.author ? { author: options.author } : undefined),
    },
    { type: 'text-start', id: partId },
    ...splitText(text).map((delta) => ({
      type: 'text-delta',
      id: partId,
      delta,
    })),
    { type: 'text-end', id: partId },
    { type: 'finish', messageId, finishReason },
  ];
}

export function createChunkStream(chunks, options = {}) {
  const {
    delayMs = 170,
    errorAfterChunk,
    error = new Error('Demo stream failed.'),
  } = options;
  let cancelled = false;
  const timers = [];

  return new ReadableStream({
    start(controller) {
      let didFinish = false;

      chunks.forEach((chunk, index) => {
        const timerId = setTimeout(
          () => {
            if (didFinish || cancelled) {
              return;
            }

            if (errorAfterChunk != null && index === errorAfterChunk) {
              didFinish = true;
              try {
                controller.error(error);
              } catch {
                // Already closed/cancelled by the consumer; ignore.
              }
              return;
            }

            try {
              controller.enqueue(chunk);
            } catch {
              // Stream was closed/cancelled between scheduling and firing — bail out.
              cancelled = true;
              return;
            }

            if (index === chunks.length - 1) {
              didFinish = true;
              try {
                controller.close();
              } catch {
                // Already closed by the consumer; ignore.
              }
            }
          },
          delayMs * (index + 1),
        );
        timers.push(timerId);
      });
    },
    cancel() {
      cancelled = true;
      timers.forEach(clearTimeout);
    },
  });
}

export function getMessageText(message) {
  return message.parts
    .map((part) => {
      if (part.type === 'text' || part.type === 'reasoning') {
        return part.text;
      }

      if (part.type === 'tool' || part.type === 'dynamic-tool') {
        return `${part.toolInvocation.toolName} (${part.toolInvocation.state})`;
      }

      if (part.type === 'file') {
        return part.filename ?? part.url;
      }

      if (part.type === 'source-url') {
        return part.title ?? part.url;
      }

      if (part.type === 'source-document') {
        return part.title ?? part.text ?? 'Source document';
      }

      if (part.type === 'step-start') {
        return 'Step started';
      }

      if (part.type.startsWith('data-') && 'data' in part) {
        return JSON.stringify(part.data);
      }

      return JSON.stringify(part);
    })
    .join('\n');
}

export function createEchoAdapter(options = {}) {
  const { agent } = options;
  const agentName = agent?.displayName ?? 'Assistant';
  const delayMs = options.delayMs ?? 170;
  const respond = options.respond ?? ((text) => `${agentName} received "${text}".`);

  return {
    async sendMessage({ message }) {
      const textOnly = message.parts
        .map((part) => (part.type === 'text' ? part.text : null))
        .filter(Boolean)
        .join('\n');
      const responseText = respond(textOnly || getMessageText(message));

      return createChunkStream(
        createTextResponseChunks(
          randomId(),
          responseText,
          agent ? { author: agent } : undefined,
        ),
        { delayMs },
      );
    },
  };
}

export function syncConversationPreview(conversations, conversationId, messages) {
  const lastMessage = messages[messages.length - 1];
  const preview =
    lastMessage == null
      ? undefined
      : getMessageText(lastMessage).split('\n')[0]?.slice(0, 70);

  return conversations.map((conversation) => {
    if (conversation.id !== conversationId) {
      return conversation;
    }

    return {
      ...conversation,
      subtitle: preview ?? conversation.subtitle,
      lastMessageAt: lastMessage?.createdAt ?? conversation.lastMessageAt,
    };
  });
}
